import { useCallback, useState } from "react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"

import type {
  BucketClickData,
  DashboardWidget,
  SelectedPointState,
} from "../widgets/types/core"
import {
  isUploadedConversationSource,
  resolveDataSource,
} from "../widgets/utils/dataSource"

const log = logger("PointSelection")

export const usePointSelection = (widgets: DashboardWidget[]) => {
  const { trackEvent } = useAnalytics()

  // State for point selection and details panel
  const [selectedPointState, setSelectedPointState] =
    useState<SelectedPointState | null>(null)
  const [chartPoints, setChartPoints] = useState<
    Record<string, BucketClickData[]>
  >({})

  // Handle point clicks from chart widgets
  const handlePointClick = useCallback(
    (point: BucketClickData, widgetId: string, metricId?: string) => {
      // Validate the point data
      if (!point?.timestamp) {
        log.warn("Invalid point data received", { point })
        return
      }

      // Find the widget to get its config
      const widget = widgets.find((w) => w.id === widgetId)
      const finalMetricId = metricId || widget?.config?.metricId
      const dataSource = resolveDataSource(widget?.config ?? {})
      const isMonitoring = isUploadedConversationSource(dataSource)

      if (!finalMetricId) {
        log.warn("No metricId found for widget", { widgetId })
        return
      }

      setSelectedPointState({
        point,
        widgetId,
        metricId: finalMetricId,
        isMonitoring,
        dataSource,
      })

      // Track point selection and run details opened
      trackEvent(TRACKING_EVENTS.DASHBOARD.POINT_SELECTION_MADE, {
        widget_id: widgetId,
        metric_id: finalMetricId,
        point_data: {
          timestamp: point.timestamp,
          bucket_count: point.bucketCount,
        },
      })

      trackEvent(TRACKING_EVENTS.DASHBOARD.RUN_DETAILS_OPENED, {
        run_id: point.timestamp, // Using timestamp as proxy for run_id
        widget_id: widgetId,
        metric_id: finalMetricId,
      })
    },
    [widgets, trackEvent],
  )

  // Get the selected point for a specific widget
  const getSelectedPointForWidget = useCallback(
    (widgetId: string) => {
      if (selectedPointState?.widgetId !== widgetId) {
        return null
      }
      return selectedPointState.point
    },
    [selectedPointState],
  )

  const clearSelection = useCallback(() => {
    if (selectedPointState) {
      // Track run details closed
      trackEvent(TRACKING_EVENTS.DASHBOARD.RUN_DETAILS_LOADED, {
        run_id: selectedPointState.point.timestamp, // Using timestamp as proxy for run_id
      })
    }
    setSelectedPointState(null)
  }, [selectedPointState, trackEvent])

  return {
    selectedPointState,
    setSelectedPointState,
    chartPoints,
    setChartPoints,
    handlePointClick,
    getSelectedPointForWidget,
    clearSelection,
  }
}
